import { Input } from 'antd';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../AppContext';
import {
  DISALLOWED_KEYS,
  MODULES_KEY,
  PERMISSION_OBJECT,
  PREVIOUS_WEEK_ALLOCATED_HOURS_KEY,
  ROLE_KEYS,
} from '../../../common/constants';
import VerifyPermissions from '../../../components/VerifyPermissions';

dayjs.extend(isoWeek);

const InputCellRenderer = ({ node, colDef, result, handleUpdateRecord }) => {
  const {
    state: { currentUser },
  } = useContext(AppContext);
  const inputRef = useRef(null);
  const prevInputRef = useRef(null);
  const [inputKey, setInputKey] = useState(null);
  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.RESOURCE_ALLOCATION,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const addResourceAllocationTime =
    process.env.REACT_APP_RESOURCE_ALLOCATION_DISABLED_TIME;
  const addResourceAllocationMinute =
    process.env.REACT_APP_RESOURCE_ALLOCATION_DISABLED_MINUTE;
  const addResourceAllocationDay =
    process.env.REACT_APP_RESOURCE_ALLOCATION_DISABLED_DAY;
  const record = node?.data;

  const oldValue = node?.data?.hoursAllocation?.find(
    (item) => item?.projectId === colDef?.field,
  )?.hours;

  const previousWeekAllocatedHours = node?.data?.previousWeekAllocatedHours?.find(
    (item) => item?.projectId === colDef?.field,
  )?.previousWeeklyHours;

  const [value, setValue] = useState(
    node?.data?.hoursAllocation?.find(
      (item) => item?.projectId === colDef?.field,
    )?.hours,
  );
  const handleInputChange = (e) => {
    const inputValue = e?.target?.value;
    const numericValue = parseInt(inputValue?.slice(0, 2), 10);
    setValue(!Number.isNaN(numericValue) ? numericValue : undefined);
  };

  const handleInputBlur = () => {
    if (value !== oldValue) {
      handleUpdateRecord(value, colDef?.field, record, colDef?.headerName);
    }
  };

  const handleInputFocus = () => {
    // eslint-disable-next-line no-undef
    prevInputRef.current = document?.activeElement;
  };

  const handleKeyDown = (e) => {
    if (DISALLOWED_KEYS?.includes(e?.key)) {
      e?.preventDefault();
    }

    if (e?.key === PREVIOUS_WEEK_ALLOCATED_HOURS_KEY) {
      setValue(previousWeekAllocatedHours);
    }
    setInputKey(e?.key);
  };

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const cellElement = document?.querySelector('.ag-cell-focus');
    if (cellElement) {
      const inputElement = cellElement?.querySelector('input');
      if (inputElement) {
        inputElement?.focus();
      }
    }
  }, [value, inputKey]);

  const isPastWeek = (dateStr) => {
    const inputDate = dayjs(dateStr);
    const now = dayjs();

    if (inputDate.isoWeekYear() < now.isoWeekYear()) return true;
    if (
      inputDate.isoWeekYear() === now.isoWeekYear() &&
      inputDate.isoWeek() < now.isoWeek()
    ) {
      return true;
    }
    return false;
  };

  const isCurrentWeek = (dateStr) => {
    const inputDate = dayjs(dateStr);
    const now = dayjs();
    return (
      inputDate.isoWeek() === now.isoWeek() &&
      inputDate.isoWeekYear() === now.isoWeekYear()
    );
  };

  // Friday (ISO weekday 5) 4:30 PM IST deadline, kept in sync with the
  // backend restriction in CreateResourceProjectAllocation / UpdateProjectMember.
  const isAfterWeeklyDeadline = () => {
    const now = dayjs();
    const disabledIsoDay = Number(addResourceAllocationDay);
    const disabledHour = Number(addResourceAllocationTime);
    const disabledMinute = Number(addResourceAllocationMinute) || 0;
    return (
      now.isoWeekday() > disabledIsoDay ||
      (now.isoWeekday() === disabledIsoDay &&
        (now.hour() > disabledHour ||
          (now.hour() === disabledHour && now.minute() >= disabledMinute)))
    );
  };

  const addHrsPermission = () => {
    if ([ROLE_KEYS.ADMIN, ROLE_KEYS.SUPER_ADMIN]?.includes(currentUser?.role)) {
      return false;
    }

    const dateStr = dayjs(result)?.format();

    if (isPastWeek(dateStr)) {
      return true;
    }

    if (!isCreatePermission) {
      return false;
    }

    if (isCurrentWeek(dateStr)) {
      return isAfterWeeklyDeadline();
    }

    return false;
  };

  return (
    <>
      {!!previousWeekAllocatedHours && (
        <div
          className="previous-week-hours pointer"
          onClick={() => {
            if (addHrsPermission()) return;
            setValue(previousWeekAllocatedHours);
          }}
        >
          {previousWeekAllocatedHours}
        </div>
      )}
      <Input
        ref={inputRef}
        disabled={addHrsPermission()}
        className="resource-allocation-input"
        value={parseInt(value, 10) || ''}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onPressEnter={() => {
          if (inputRef?.current) inputRef?.current?.blur();
        }}
        onKeyDown={handleKeyDown}
      />
    </>
  );
};

export default InputCellRenderer;
